import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CasinoIcon from "@mui/icons-material/Casino";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import { Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVideo } from "../../../../context/juno/VideoContext";
import { isPromptInputDisabled } from "../../../../utils/validation/videoValidation";
import { GradationButton } from "../../../../utils/gradationButton";
import { RowBox } from "../../../../utils/styledBox";
import InputPreset from "../../Images/components/InputPrompt/InputPreset";
import CameraMovementPreset from "./CameraMovementPreset";
import { PromptEnhancementModal } from "../../components/PromptEnhancementModal";

interface VideoPromptInputProps {
  updating: boolean;
  rotationDegree: number;
  onGenerateSample: () => void;
  // 送信ボタン関連
  onSubmit: () => void;
  disable3sec: boolean;
  totalCredit: number | string | undefined;
  isCalculatingVideoCredit: boolean;
  isCalculatingSoundCredit: boolean;
}

const VideoPromptInput: React.FC<VideoPromptInputProps> = ({
  updating,
  rotationDegree,
  onGenerateSample,
  onSubmit,
  disable3sec,
  totalCredit,
  isCalculatingVideoCredit,
  isCalculatingSoundCredit,
}) => {
  const { t } = useTranslation();
  const { prompt, setPrompt, aiEngine, menu, modelVersion, image1 } = useVideo();
  const [localPrompt, setLocalPrompt] = useState<string>("");
  const [enhancementModalOpen, setEnhancementModalOpen] = useState(false);

  // localPromptを安全な文字列として取得
  const safeLocalPrompt = typeof localPrompt === "string" ? localPrompt : String(localPrompt || "");

  // 外部からのプロンプト変更をローカルに反映（メニューがupscaleまたはlipsyncの場合は更新しない）
  useEffect(() => {
    if (!isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])) {
      // promptが文字列でない場合の安全な処理
      const promptStr = typeof prompt === "string" ? prompt : String(prompt || "");
      setLocalPrompt(promptStr);
    }
  }, [prompt, menu, aiEngine, modelVersion]);

  // ローカルプロンプト変更時の処理（入力中）
  const handleLocalPromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalPrompt(newValue);
  };

  // フォーカスアウト時の処理
  const handlePromptBlur = () => {
    if (!isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])) {
      setPrompt(localPrompt);
    }
  };

  const handleGenerateSample = () => {
    onGenerateSample();
  };

  const handleUpdatePrompt = () => {
    setEnhancementModalOpen(true);
  };

  const handleEnhancementInsert = (enhancedPrompt: string) => {
    const promptStr = typeof enhancedPrompt === "string" ? enhancedPrompt : String(enhancedPrompt || "");
    setLocalPrompt(promptStr);
    setPrompt(promptStr);
  };

  const handleDeletePrompt = () => {
    setLocalPrompt("");
    setPrompt("");
  };

  const handleSetPrompt = (newPrompt: string) => {
    const promptStr = typeof newPrompt === "string" ? newPrompt : String(newPrompt || "");
    setLocalPrompt(promptStr);
    setPrompt(promptStr);
  };

  return (
    <>
      <RowBox sx={{ gap: 2, alignItems: "flex-end", mb: 1 }}>
        <TextField
          variant="standard"
          autoComplete={"off"}
          placeholder={t("juno.video.params.input.placeholder")}
          autoFocus
          value={
            isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])
              ? t("juno.video.params.input.notRequired")
              : safeLocalPrompt
          }
          onChange={handleLocalPromptChange}
          onBlur={handlePromptBlur}
          disabled={isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])}
          multiline
          fullWidth
          maxRows={8}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography variant="body2" color="secondary" sx={{ ml: 1, display: { xs: "none", sm: "block" } }}>
                  @{aiEngine}:
                </Typography>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 2,
              backgroundColor: "transparent",
              overflow: "hidden",
            },
            inputProps: { maxLength: aiEngine === "RUNWAY" ? 512 : 1000 },
          }}
          sx={{
            "& .MuiInput-root": {
              "&:before, &:after": {
                border: "none",
                display: "none",
              },
            },
          }}
        />
      </RowBox>
      <RowBox sx={{ gap: 2, justifyContent: "space-between", mt: 0.5 }}>
        <Box sx={{ gap: 2, display: "flex", flexDirection: "row", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
            {/* サイコロアイコン（常に表示、プロンプトが入力されている場合はdisabled） */}
            <Tooltip title={t("juno.history.dice")}>
              <span>
                <IconButton
                  onClick={handleGenerateSample}
                  disabled={updating || menu === "upscale" || menu === "lipsync" || safeLocalPrompt.trim().length > 0}
                >
                  <CasinoIcon
                    sx={{
                      transform: `rotate(${rotationDegree}deg)`,
                      transition: "transform 1s ease-in-out",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            {/* マジックブラシアイコン */}
            <Tooltip title={t("juno.enhancement.tooltip")}>
              <span>
                <IconButton
                  onClick={handleUpdatePrompt}
                  disabled={isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])}
                >
                  <AutoFixHighIcon />
                </IconButton>
              </span>
            </Tooltip>

            <InputPreset
              prompt={safeLocalPrompt}
              setPrompt={handleSetPrompt}
              position="bottom"
              disabled={isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine]) || updating}
            />
            <CameraMovementPreset
              prompt={safeLocalPrompt}
              setPrompt={handleSetPrompt}
              position="bottom"
              disabled={isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine]) || updating || aiEngine == "LUMA"}
            />

            {/* delete */}
            <Tooltip title={t("common.delete")}>
              <span>
                <IconButton
                  onClick={handleDeletePrompt}
                  disabled={isPromptInputDisabled(aiEngine, menu, modelVersion[aiEngine])}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {aiEngine === "RUNWAY" && (
            <Typography variant="caption" color={safeLocalPrompt.length > 512 ? "error" : "textSecondary"}>
              {safeLocalPrompt.length}/512
            </Typography>
          )}
          {aiEngine !== "RUNWAY" && (
            <Typography variant="caption" color={safeLocalPrompt.length > 1000 ? "error" : "textSecondary"}>
              {safeLocalPrompt.length}/1000
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: { xs: "none", sm: "block" } }}>
          {t("juno.video.params.input.warning")}
        </Typography>

        <GradationButton
          onClick={onSubmit}
          sx={{ minWidth: { xs: 40, sm: 240 }, px: 2 }}
          startIcon={disable3sec ? <CircularProgress size={20} /> : <SendIcon />}
          disabled={disable3sec}
        >
          <Typography sx={{ display: { xs: "none", sm: "block" } }}>{t("juno.video.params.submit")}</Typography>
          {isCalculatingVideoCredit || isCalculatingSoundCredit ? (
            <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
          ) : (
            `(${totalCredit})`
          )}
        </GradationButton>
      </RowBox>

      <PromptEnhancementModal
        open={enhancementModalOpen}
        onClose={() => setEnhancementModalOpen(false)}
        onInsert={handleEnhancementInsert}
        initialPrompt={safeLocalPrompt}
        aiEngine={aiEngine}
        modelVersion={modelVersion[aiEngine]}
        mode="video"
        promptImage={image1}
      />
    </>
  );
};

export default VideoPromptInput;
